import { DialogFooter, ITextFieldStyles } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPDefaultButton, IPModal, IPPrimaryButton, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { globalStackTokensGapMedium } from 'globalStyles';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

interface IModifyTextDialogProps {
  isActionPending?: boolean;
  value?: string;
  isOpen: boolean;
  title: string;
  label?: string;
  placeholder?: string;
  multiline?: boolean;
  width?: number | string;
  maxLength?: number;
  required?: boolean;
  textFieldStyles?: Partial<ITextFieldStyles>;
  rows?: number;
  // the primary button; Save when not given
  saveButtonText?: string;
  onClose: () => void;
  onUpdate: (newValue: string) => void;
  onSave: (newValue: string | undefined) => void;
  onGetErrorMessage?: (value: string) => string | undefined;
}

const ModifyTextDialog = (props: IModifyTextDialogProps) => {
  const { t } = useTranslation(['translation']);
  const appContext = useContext(AppContext);

  const cannotSave = (): boolean => {
    if (props.isActionPending) return true;
    if (props.onGetErrorMessage && props.onGetErrorMessage(props.value || '') !== undefined) return true;
    if (props.required && (!props.value || props.value?.trim() === '')) return true;

    return false;
  };

  return (
    <IPModal isBlocking={false} isOpen={props.isOpen} onDismiss={props.onClose}>
      <IPStack
        styles={{ root: { padding: 20, minWidth: 300, width: props.width ?? 300 } }}
        tokens={globalStackTokensGapMedium}
      >
        <IPText variant="xLarge">{props.title}</IPText>
        <IPTextField
          autoFocus={!appContext.isMobileView}
          required={props.required}
          styles={props.textFieldStyles}
          label={props.label}
          placeholder={props.placeholder}
          value={props.value}
          multiline={props.multiline}
          maxLength={props.maxLength}
          rows={props.rows || 10}
          onGetErrorMessage={props.onGetErrorMessage}
          onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
            props.onUpdate(newValue ? newValue : '');
          }}
        />
        <DialogFooter>
          <IPPrimaryButton
            disabled={cannotSave()}
            onClick={() => {
              props.onSave(props.value);
              props.onClose();
            }}
            text={props.saveButtonText ?? t('translation:General.Button.Save')}
          />
          <IPDefaultButton
            disabled={props.isActionPending}
            onClick={() => {
              props.onClose();
            }}
            text={t('translation:General.Button.Cancel')}
          ></IPDefaultButton>
        </DialogFooter>
      </IPStack>
    </IPModal>
  );
};

export default ModifyTextDialog;
